import { cancelQueryReads } from '../../utils/api/cancel-query-reads.util';
import { useCallback, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { ScheduleQueryInvalidation } from '../../types/query/debounced-query-invalidation.types';

/**
 * Debounces active-query recovery and silently cancels older reads without reverting cache.
 * @param delayMs - Caller-owned delay before cancellation and active refetch.
 * @returns A scheduler whose pending work is cleared on owner disposal.
 */
export const useDebouncedQueryInvalidation = (
  delayMs: number
): ScheduleQueryInvalidation => {
  const queryClient = useQueryClient();
  const timersRef = useRef<Map<string, number>>(new Map());
  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      timers.forEach((timer) => window.clearTimeout(timer));
      timers.clear();
    };
  }, [queryClient, delayMs]);

  return useCallback(
    (key, filters) => {
      const existingTimer = timersRef.current.get(key);
      if (existingTimer !== undefined) window.clearTimeout(existingTimer);
      const timer = window.setTimeout(() => {
        timersRef.current.delete(key);
        cancelQueryReads(queryClient, filters);
        void queryClient.invalidateQueries({
          ...filters,
          refetchType: 'active',
        });
      }, delayMs);
      timersRef.current.set(key, timer);
    },
    [queryClient, delayMs]
  );
};
